import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Document_vente, Ligne_Document_Vente, GeneriqueData } from '../models/models'; // Assure-toi que ce modèle existe
import api from './api';

interface DocumentVenteState {
  documentData: Document_vente[];
  total: number;
  totalPages: number;
  currentPage: number;
  totalHt: number;    // Σ HT sur tout le jeu filtré (hors pagination)
  totalTtc: number;   // Σ TTC sur tout le jeu filtré (hors pagination)
  totalHtCumul: number;  // Σ HT  des pages 1 → page courante (filtres + pagination)
  totalTtcCumul: number; // Σ TTC des pages 1 → page courante (filtres + pagination)
  loading: boolean;
  cloturing: boolean;
  decloturing: boolean;
  duplicating: boolean;
  error: string | null;
  successMessage: string | null;
  selectedDocument: Document_vente | null;
  // Transient fabrication — dépôt global des composants choisi sur le document de
  // fabrication courant. Injecté dans chaque ligne ARTICLE sauvegardée (depot_composant_id).
  composantDepot: GeneriqueData | null;
  filters: DocumentVenteFetchParams;
  // Suggestions de l'autocomplete « Numéro de document » (filtre) — état séparé de
  // documentData/loading pour ne pas écraser le tableau ni hériter de son spinner.
  numeroDocOptions: Document_vente[];
  numeroDocLoading: boolean;
}

const initialState: DocumentVenteState = {
  documentData: [],
  total: 0,
  totalPages: 0,
  currentPage: 0,
  totalHt: 0,
  totalTtc: 0,
  totalHtCumul: 0,
  totalTtcCumul: 0,
  loading: false,
  cloturing: false,
  decloturing: false,
  duplicating: false,
  error: null,
  successMessage: null,
  selectedDocument: null,
  composantDepot: null,
  filters: {
    type_doc: null,
    contrat_id: null,
    numero_doc: null,
    tiers_id: null,
    depot_id: null,
    departement_id: null,
    date_debut: null,
    date_fin: null,
    statut_id: null,
    reference: null,
    limit: 5,
    offset: 0,
  },
  numeroDocOptions: [],
  numeroDocLoading: false,
};

export interface DocumentVenteFetchParams {
  type_doc: number | null;
  contrat_id?: number | null;
  numero_doc?: string | null;
  tiers_id?: number | null;
  depot_id?: number | null;
  departement_id?: number | null;
  date_debut?: string | null;
  date_fin?: string | null;
  statut_id?: number | null;
  reference?: string | null;
  limit: number;
  offset: number;
}


// 🔄 Fetch all Documents de Vente
export const fetchDocumentVenteData = createAsyncThunk<
  { data: Document_vente[]; total: number; totalPages: number; currentPage: number; totalHt?: number; totalTtc?: number; totalHtCumul?: number; totalTtcCumul?: number },
  {
    type_doc: number | null;
    contrat_id?: number | null;
    numero_doc?: string | null;
    tiers_id?: number | null;
    depot_id?: number | null;
    departement_id?: number | null;
    date_debut?: string | null;
    date_fin?: string | null;
    reference?: string | null;
    limit: number;
    offset: number;
  },
  { rejectValue: string }
>(
  'documentVente/fetchDocumentVenteData',
  async (params, thunkAPI) => {
    try {
      // signal : la page annule la requête précédente quand une autre page est demandée
      const response = await api.get('/documentVentes/getAll', {
        params,
        signal: thunkAPI.signal,
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to load documents.');
    }
  }
);

// 🔢 Suggestions de numéros pour l'autocomplete du filtre (n'alimente pas le tableau)
export const searchNumeroDoc = createAsyncThunk<Document_vente[], string, { rejectValue: string }>(
  'documentVente/searchNumeroDoc',
  async (numero_doc, thunkAPI) => {
    try {
      const response = await api.get('/documentVentes/getAll', {
        params: { numero_doc, type_doc: null, limit: 10, offset: 0 },
        signal: thunkAPI.signal,
      });
      return response.data.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to load documents.');
    }
  }
);

// ✍️ Create or update Document
export const createDocumentVente = createAsyncThunk<Document_vente, Document_vente, { rejectValue: string }>(
  'documentVente/createOrUpdate',
  async (docData, thunkAPI) => {
    try {
      const response = await api.post('/documentVentes/createOrUpdate', docData);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to create or update.');
    }
  }
);

// 🔀 Dispatch vers document suivant
export const dispatchToNextDoc = createAsyncThunk<
  Document_vente,
  { document: Document_vente; lignes: Array<Ligne_Document_Vente & { source_ligne_id: number }> },
  { rejectValue: string }
>(
  'documentVente/dispatchToNextDoc',
  async (payload, thunkAPI) => {
    try {
      const response = await api.post('/documentVentes/dispatchToNextDoc', payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec de la création du document suivant.');
    }
  }
);

// 🔀 Dispatch multiple groupes (un document par dépôt) dans une seule transaction
export const dispatchMultipleToNextDoc = createAsyncThunk<
  Document_vente[],
  { groupes: Array<{ document: Document_vente; lignes: Array<Ligne_Document_Vente & { source_ligne_id: number }> }> },
  { rejectValue: string }
>(
  'documentVente/dispatchMultipleToNextDoc',
  async (payload, thunkAPI) => {
    try {
      const response = await api.post('/documentVentes/dispatchMultipleToNextDoc', payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec de la création des documents suivants.');
    }
  }
);

// 🧬 Duplique un document (en-tête + lignes) vers un type cible choisi par l'utilisateur
export const duplicateDocumentVente = createAsyncThunk<
  Document_vente,
  { source_id: number; doc_code: string; tiers_id: number | null; depot_id: number; reference: string | null; date_doc: string; depot_composant_id: number | null },
  { rejectValue: string }
>(
  'documentVente/duplicate',
  async (payload, thunkAPI) => {
    try {
      const response = await api.post('/documentVentes/duplicate', payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec de la duplication.');
    }
  }
);

// ❌ Delete document
// document_vente_id = clé du document visé (numero_doc n'est pas unique entre départements)
export const deleteDocumentVente = createAsyncThunk<string, { document_vente_id: number; numero_doc: string; doc_code: string }, { rejectValue: string }>(
  'documentVente/delete',
  async ({ document_vente_id, numero_doc, doc_code }, thunkAPI) => {
    try {
      await api.delete('/documentVentes', { params: { document_vente_id, numero_doc, doc_code } });
      return numero_doc;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to delete.');
    }
  }
);

// 🔒 Clôture document (verrou — non modifiable ensuite ; recalcul CMUP si doc d'achat)
export const cloturerDocumentVente = createAsyncThunk<
  Document_vente,
  { document_vente_id: number; numero_doc: string; doc_code: string },
  { rejectValue: string }
>(
  'documentVente/cloturer',
  async ({ document_vente_id, numero_doc, doc_code }, thunkAPI) => {
    try {
      const response = await api.post('/documentVentes/cloturer', { document_vente_id, numero_doc, doc_code });
      return response.data?.document ?? response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec de la clôture.');
    }
  }
);

// 🔓 Déclôture document (lève le verrou — redevient modifiable ; n'altère pas le CMUP)
export const decloturerDocumentVente = createAsyncThunk<
  Document_vente,
  { document_vente_id: number; numero_doc: string; doc_code: string },
  { rejectValue: string }
>(
  'documentVente/decloturer',
  async ({ document_vente_id, numero_doc, doc_code }, thunkAPI) => {
    try {
      const response = await api.post('/documentVentes/decloturer', { document_vente_id, numero_doc, doc_code });
      return response.data?.document ?? response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec de la déclôture.');
    }
  }
);

// 🔎 Fetch un document par id (clé numérique — jamais numero_doc, pas unique par owner)
// Utilisé par la page détail à route dédiée (/facturation/:id) — voir DocumentVenteDetailPage.
export const fetchDocumentById = createAsyncThunk<Document_vente, number, { rejectValue: string }>(
  'documentVente/fetchById',
  async (id, thunkAPI) => {
    try {
      const response = await api.get(`/documentVentes/${id}`, { signal: thunkAPI.signal });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to load document.');
    }
  }
);

const documentVenteSlice = createSlice({
  name: 'documentVente',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setSelectedDocument: (state, action: PayloadAction<Document_vente | null>) => {
      state.selectedDocument = action.payload;
      // Reset du dépôt composants au changement de document — la page le ré-initialise
      // depuis les lignes COMPOSANT existantes du document ouvert.
      state.composantDepot = null;
    },
    setComposantDepot: (state, action: PayloadAction<GeneriqueData | null>) => {
      state.composantDepot = action.payload;
    },
    clearDocumentData: (state) => {
      state.documentData = [];
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    setFilters: (state, action: PayloadAction<Partial<DocumentVenteFetchParams>>) => {
      // Tout changement de filtre (payload sans `offset`) repasse la pagination
      // a la page 1 — evite de rester sur une page devenue vide apres filtrage.
      // Un changement de page (handlePageChange envoie `offset`) preserve l'offset.
      const isPageChange = 'offset' in action.payload;
      state.filters = {
        ...state.filters,
        ...action.payload,
        ...(!isPageChange && { offset: 0 }),
      };
    },
    resetFilter: (state) => {
      state.filters = {
        type_doc: null,
        contrat_id: null,
        numero_doc: null,
        tiers_id: null,
        depot_id: null,
        date_debut: null,
        date_fin: null,
        reference: null,
        limit: 5,
        offset: 0,
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocumentVenteData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDocumentVenteData.fulfilled, (state, action) => {
        state.loading = false;
        state.documentData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
        state.totalHt = action.payload.totalHt ?? 0;
        state.totalTtc = action.payload.totalTtc ?? 0;
        state.totalHtCumul = action.payload.totalHtCumul ?? 0;
        state.totalTtcCumul = action.payload.totalTtcCumul ?? 0;
      })
      .addCase(fetchDocumentVenteData.rejected, (state, action) => {
        // Requête annulée (remplacée par une plus récente) : ni erreur, ni fin de chargement
        if (action.meta.aborted) return;
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(searchNumeroDoc.pending, (state) => {
        state.numeroDocLoading = true;
      })
      .addCase(searchNumeroDoc.fulfilled, (state, action) => {
        state.numeroDocLoading = false;
        state.numeroDocOptions = action.payload;
      })
      .addCase(searchNumeroDoc.rejected, (state, action) => {
        // Recherche annulée (remplacée par une frappe plus récente) : on reste en chargement
        if (action.meta.aborted) return;
        state.numeroDocLoading = false;
        state.numeroDocOptions = [];
      })
      .addCase(createDocumentVente.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createDocumentVente.fulfilled, (state, action: PayloadAction<Document_vente>) => {
        state.loading = false;
        state.successMessage = 'Création/Mise à jour réussie !';
        state.selectedDocument = action.payload;
      })
      .addCase(createDocumentVente.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteDocumentVente.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteDocumentVente.fulfilled, (state, action: PayloadAction<String>) => {
        state.loading = false;
        state.successMessage = 'Suppression réussie !';
        state.documentData = state.documentData.filter(doc => doc.numero_doc !== action.payload);
        state.selectedDocument = null;
        state.total = Math.max(0, state.total - 1);
      })
      .addCase(deleteDocumentVente.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(dispatchToNextDoc.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(dispatchToNextDoc.fulfilled, (state, action: PayloadAction<Document_vente>) => {
        state.loading = false;
        state.successMessage = 'Document suivant créé avec succès !';
      })
      .addCase(dispatchToNextDoc.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(duplicateDocumentVente.pending, (state) => {
        state.duplicating = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(duplicateDocumentVente.fulfilled, (state) => {
        state.duplicating = false;
        state.successMessage = 'Document dupliqué avec succès !';
      })
      .addCase(duplicateDocumentVente.rejected, (state, action) => {
        state.duplicating = false;
        state.error = action.payload as string;
      })
      .addCase(cloturerDocumentVente.pending, (state) => {
        state.cloturing = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(cloturerDocumentVente.fulfilled, (state, action) => {
        state.cloturing = false;
        state.successMessage = 'Document clôturé.';
        // Identifier le doc clôturé par le numero_doc passé en argument du thunk,
        // pas par action.payload.id : selon la config du driver mariadb, le payload
        // peut être null (resultset OK de l'UPDATE consommé en premier) ou avoir un
        // id BigInt/string incomparable au number du state.
        const numeroDoc = action.meta.arg.numero_doc;
        if (state.selectedDocument?.numero_doc === numeroDoc) {
          state.selectedDocument = { ...state.selectedDocument, cloture: 1 };
        }
        const i = state.documentData.findIndex(d => d.numero_doc === numeroDoc);
        if (i !== -1) state.documentData[i] = { ...state.documentData[i], cloture: 1 };
      })
      .addCase(cloturerDocumentVente.rejected, (state, action) => {
        state.cloturing = false;
        state.error = action.payload as string;
      })
      .addCase(decloturerDocumentVente.pending, (state) => {
        state.decloturing = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(decloturerDocumentVente.fulfilled, (state, action) => {
        state.decloturing = false;
        state.successMessage = 'Document déclôturé.';
        // Même précaution que pour la clôture : on cible par numero_doc (payload.id
        // potentiellement null/BigInt selon le driver mariadb).
        const numeroDoc = action.meta.arg.numero_doc;
        if (state.selectedDocument?.numero_doc === numeroDoc) {
          state.selectedDocument = { ...state.selectedDocument, cloture: 0 };
        }
        const i = state.documentData.findIndex(d => d.numero_doc === numeroDoc);
        if (i !== -1) state.documentData[i] = { ...state.documentData[i], cloture: 0 };
      })
      .addCase(decloturerDocumentVente.rejected, (state, action) => {
        state.decloturing = false;
        state.error = action.payload as string;
      })
      .addCase(fetchDocumentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDocumentById.fulfilled, (state, action: PayloadAction<Document_vente>) => {
        state.loading = false;
        state.selectedDocument = action.payload;
      })
      .addCase(fetchDocumentById.rejected, (state, action) => {
        state.loading = false;
        // Chargement annulé (page quittée / id changé) : pas une erreur
        if (action.meta.aborted) return;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCurrentPage,
  setSelectedDocument,
  setComposantDepot,
  clearDocumentData,
  clearMessages,
  setFilters,
  resetFilter,
} = documentVenteSlice.actions;

export default documentVenteSlice.reducer;
